import { useEffect, useMemo, useRef, useState } from 'react'
import { api, periodenname } from './api'
import { Auswahlfeld } from './Auswahlfeld'
import { fachlicherSatz } from './Fehlersatz'
import { meldeErfolg } from './Erfolgsstelle'
import { anwendername, bewertungsebenenname, gesellschaftsname, ohneVortrag, t, zeitpunkt, anzahlwort, dezimalzahl } from './sprache'
import type { Sprachschluessel } from './sprache'
import { Dateiwahl } from './Dateiwahl'
import { Dialogfenster } from './Dialogfenster'
import { Blattabschnitt, Stammdaten } from './Stammdaten'
import { Zustandssymbol } from './Zustand'
import type { Zustandsart } from './Zustand'
import type { Datenquelle, Dateiprobe, Importlaufkopf, Listeneintrag, Stammdatenblatt, Zielfeld } from './typen'
import {
  BEWEGUNGSART_ANFANGSBESTAND,
  KONTOTRENNUNGEN,
  PERIODENMUSTER,
  abbildungLesen,

  abbildungSchreiben,
  feldname,
  feldzustand,
  gruppen,
  istGerechneteBewegungsart,
  mitWertenAbWerk,
  spaltenname,
  spaltenzeilen,
  vollstaendigerVorschlag,
  vorratsname,
  zuordnungsstand,
} from './Datenquellen'
import { Vorschlagsliste, vorschlagszusatz } from './Bewegungsartvorschlaege'
import type { Abbildung, Feldbindung, Spaltenzeile } from './Datenquellen'
import { Spaltenmassleiste, useSpaltenmasse } from './Spaltenbreiten'
import { useDarf } from './Rechte'

/**
 * Der Bereich Datenquellen (5.1 Abschnitt 3.7, R521–R527).
 *
 * **Einrichtung, kein Tagesbildschirm** (R526): Eine Quelle wird einmal
 * angelegt und dann bei jedem Import benutzt; angesehen wird sie selten.
 * Deshalb keine Kontextzeile, keine Kennzahlen — links das Verzeichnis, in
 * der Mitte das Stammdatenblatt (R317) mit den Abschnitten, die eine Quelle
 * über ein gewöhnliches Stammdatum hinaus hat: *Die Datei*, *Die Zuordnung*,
 * *Verwendung*. Sie klappen auf und tragen zugeklappt eine Auskunft (R515).
 *
 * **Die Bereitschaftstafel ist in die Liste gewandert.** Bis zum 2026-09-13
 * stand über der Pflege eine zweite Tabelle mit denselben Zeilen; sie sagte,
 * was die Liste jetzt selbst je Zeile sagt — ob die Quelle läuft (R297) und
 * wie viele Felder offen sind (R523) — und kostete eine Bildschirmhöhe.
 */
export function Datenquellen() {
  const [quellen, setQuellen] = useState<Datenquelle[]>([])
  const [zielfelder, setZielfelder] = useState<Zielfeld[]>([])
  const [stand, setStand] = useState(0)

  useEffect(() => {
    api.datenquellen().then(setQuellen)
  }, [stand])

  // Die Zielfelder kommen vom Server aus dem Modell (R522) — eine neue
  // Dimension steht hier, ohne dass jemand die Oberfläche anfasst.
  useEffect(() => {
    api.zielfelder().then(setZielfelder)
  }, [stand])

  const nachSchluessel = useMemo(() => new Map(quellen.map((q) => [q.schluessel, q])), [quellen])

  return (
    <section className="quellenbereich">
      <h1>{t('bereich.datenimport.quellen')}</h1>

      <Stammdaten
        zulaessig={['datenquelle']}
        aufAenderung={() => setStand((n) => n + 1)}
        zeilenzusatz={(eintrag: Listeneintrag) => {
          const quelle = nachSchluessel.get(eintrag.schluessel)
          return quelle ? <Quellenmarke quelle={quelle} /> : null
        }}
        zusatz={(blatt: Stammdatenblatt) => {
          const quelle = nachSchluessel.get(blatt.schluessel)
          return quelle ? (
            <Quellenabbildung
              // Eine neue Fassung ist ein neues Blatt: Der Zustand darin fängt
              // bei dem an, was gespeichert ist, nicht bei dem, was vorhin
              // eingetippt war.
              key={`${quelle.schluessel}:${quelle.fassung}`}
              quelle={quelle}
              zielfelder={zielfelder}
              geaendertAm={blatt.fassungen[0]?.erfasstAm ?? null}
              aufGespeichert={() => setStand((n) => n + 1)}
            />
          ) : null
        }}
      />
    </section>
  )
}

/** Was eine Zeile der Liste über die Quelle sagt: ob sie läuft, und was offen ist. */
function Quellenmarke({ quelle }: { quelle: Datenquelle }) {
  const art: Zustandsart = !quelle.istAktiv ? 'fehlt' : quelle.istAusfuehrbar ? 'fertig' : 'offen'
  const titel = !quelle.istAktiv
    ? t('import.zustand.stillgelegt')
    : quelle.istAusfuehrbar
      ? t('import.zustand.laeuft')
      : t('import.zustand.nichtausfuehrbar')
  const offen = quelle.stand?.offen ?? 0

  return (
    <span className="quellenmarke" title={titel}>
      <Zustandssymbol art={art} />
      {offen > 0 && <span className="offen">{t('quelle.liste.offen', { anzahl: anzahlwort(offen) })}</span>}
    </span>
  )
}

/* ======================================================================
   Die Abschnitte, die eine Datenquelle über ein Stammdatum hinaus hat
   ====================================================================== */

/**
 * Die Datei und die Zuordnung — das, was der Importdienst liest (R521).
 *
 * **Die Datei steht oben, die Felder stehen darunter** (R313): Wer eine
 * Spalte einem Feld zuordnen soll, muss die Spalte sehen — mit ihrem Inhalt.
 * Deshalb trägt jeder Spaltenkopf Buchstabe, Überschrift und den ersten Wert.
 *
 * **Fünf Handgriffe** (R136, R524): Spalte auf Feld · fester Wert aus dem
 * Vorrat · Vorzeichen umkehren · fester Faktor — und das Bündeln von Konten,
 * das im Zuordnungstisch steht. Keine Bedingungen, keine Formeln.
 *
 * **Gespeichert wird als neue Fassung** (R314): Ein Lauf, der unter der alten
 * gelesen wurde, findet sie wieder.
 */
function Quellenabbildung({
  quelle,
  zielfelder,
  geaendertAm,
  aufGespeichert,
}: {
  quelle: Datenquelle
  zielfelder: Zielfeld[]
  /** Wann die Quelle zuletzt geändert wurde — statt ihrer Fassungsnummer (7.2 Abschnitt 36, R1141). */
  geaendertAm: string | null
  aufGespeichert: () => void
}) {
  /*
    **Die Spaltenbreiten zieht der Anwender** (7.3 Abschnitt 10, R415 —
    Auftrag vom 2026-09-29). Hier je Spalte eine Breite: Die Spalten dieser
    Tabelle tragen verschiedene Dinge und haben nichts gleichzumachen.
  */
  const { masse: spaltenmasse1, rahmen: spaltenrahmen1 } =
    useSpaltenmasse<HTMLTableElement>('datenquelle:quellenabbildung:1')

  /*
    **Die Spaltenbreiten zieht der Anwender** (7.3 Abschnitt 10, R415 —
    Auftrag vom 2026-09-29). Hier je Spalte eine Breite: Die Spalten dieser
    Tabelle tragen verschiedene Dinge und haben nichts gleichzumachen.
  */
  const { masse: spaltenmasse2, rahmen: spaltenrahmen2 } =
    useSpaltenmasse<HTMLTableElement>('datenquelle:quellenabbildung:2')

  /*
    **Die Spaltenbreiten zieht der Anwender** (7.3 Abschnitt 10, R415 —
    Auftrag vom 2026-09-29). Hier je Spalte eine Breite: Die Spalten dieser
    Tabelle tragen verschiedene Dinge und haben nichts gleichzumachen.
  */
  const { masse: spaltenmasse3, rahmen: spaltenrahmen3 } =
    useSpaltenmasse<HTMLTableElement>('datenquelle:quellenabbildung:3')

  const darf = useDarf()
  const [abbildung, setAbbildung] = useState<Abbildung>(() => abbildungLesen(quelle.abbildungJson))
  const [probe, setProbe] = useState<Dateiprobe | null>(null)
  /** Ob das Zuordnungsfenster offensteht (R737) — die Arbeit geschieht dort, nicht im Blatt. */
  const [fensterAuf, setFensterAuf] = useState(false)
  const [meldung, setMeldung] = useState<string | null>(null)
  /** Unter welchem Knopf die Meldung steht — zwei Abschnitte speichern dieselbe Quelle. */
  const [meldungsort, setMeldungsort] = useState<'zuordnung' | 'vorschlaege'>('zuordnung')
  const [laeuft, setLaeuft] = useState(false)
  const [laeufe, setLaeufe] = useState<Importlaufkopf[] | null>(null)
  const datei = useRef<File | null>(null)
  const dateifeld = useRef<HTMLInputElement>(null)

  const dateiquelle = quelle.art === 'xlsx' || quelle.art === 'csv'
  const istPackage = abbildung.betragsart === 'package'

  useEffect(() => {
    api
      .importlaeufe(200)
      .then((alle) => setLaeufe(alle.filter((l) => l.datenquelle === quelle.schluessel)))
  }, [quelle.schluessel])

  // **Die Werte ab Werk stehen vorausgewählt da** (R726). Sie kommen mit den
  // Zielfeldern vom Server, und die treffen nach dem ersten Bild ein — deshalb
  // hier und nicht im Anfangszustand. Was schon gebunden ist, bleibt; ergibt
  // sich nichts zu ergänzen, gibt der Helfer dieselbe Zuordnung zurück und
  // nichts wird neu gezeichnet.
  useEffect(() => {
    setAbbildung((bisher) => mitWertenAbWerk(bisher, zielfelder))
  }, [zielfelder])

  const stand = useMemo(() => zuordnungsstand(zielfelder, abbildung), [zielfelder, abbildung])
  // Die Überschriftzeile trägt die Spaltenköpfe der Dateivorschau; den ersten
  // Datenwert braucht erst das Zuordnungsfenster (R737).
  const ueberschrift = probe?.anfang[Math.max(0, abbildung.kopfzeilen - 1)] ?? []
  function aendere(teil: Partial<Abbildung>) {
    setAbbildung((bisher) => ({ ...bisher, ...teil }))
  }

  /**
   * Was das Fenster mitbringt, sind **nur die Feldbindungen** (R737): Blatt,
   * Kopfzeilen und Trennzeichen bleiben die des Blattes, auch wenn im Fenster
   * eine Datei gelesen wurde. Übernehmen speichert in einem Zug — der
   * Anwender hat den Vorgang im Fenster abgeschlossen.
   */
  async function uebernimm(felder: Record<string, Feldbindung>, spiegelspalten: Record<number, string>) {
    const neu = { ...abbildung, felder, spiegelspalten }
    setAbbildung(neu)
    setFensterAuf(false)
    await speichere(neu)
  }

  async function lies(gewaehlt: File | null | undefined, blatt = abbildung.blatt) {
    if (!gewaehlt) {
      return
    }

    datei.current = gewaehlt
    setLaeuft(true)
    setMeldung(null)
    setMeldungsort('zuordnung')

    const felder = new FormData()
    felder.append('datei', gewaehlt)
    felder.append('trennzeichen', abbildung.trennzeichen)
    if (blatt) {
      felder.append('blatt', blatt)
    }

    try {
      const gelesen = await api.dateiprobe(felder)
      setProbe(gelesen)
      if (gelesen.befund) {
        setMeldung(gelesen.befund)
      }
      // Eine Mappe mit genau einem Blatt braucht keine Wahl — und eine mit
      // mehreren bekommt das erste vorgeschlagen, statt leer zu bleiben.
      if (!blatt && gelesen.blaetter && gelesen.blaetter.length > 0) {
        aendere({ blatt: gelesen.blaetter[0] })
      }
    } catch (f) {
      // Nur der fachliche Befund steht hier; eine Störung meldet das
      // Störungsfenster (7.2 Abschnitt 30, R1003/R1004).
      setMeldung(fachlicherSatz(f))
    } finally {
      setLaeuft(false)
    }
  }

  async function speichere(a: Abbildung = abbildung, ort: 'zuordnung' | 'vorschlaege' = 'zuordnung') {
    setLaeuft(true)
    setMeldung(null)
    setMeldungsort(ort)
    try {
      await api.abbildungSpeichern(quelle.schluessel, abbildungSchreiben(a, zielfelder))
      // Ohne „als neue Fassung — die vorige bleibt lesbar" (7.2 Abschnitt 34, R1131).
      meldeErfolg(t('erfolg.spaltenzuordnung.gespeichert'))
      aufGespeichert()
    } catch (f) {
      setMeldung(fachlicherSatz(f))
    } finally {
      setLaeuft(false)
    }
  }

  if (!dateiquelle) {
    // Eine Datenbank und ein LucaNet-Export haben keine Datei und keine
    // Spalten. Die Zielfelder sind dieselben; die Abschnitte dazu kommen,
    // sobald die Art läuft (5.1 Abschnitt 3.7, offener Punkt).
    return (
      <Blattabschnitt kennung="quelle.datei" titel={t('quelle.datei.titel')} zusatz={t('import.zustand.nichtausfuehrbar')}>
        <p className="erlaeuterung">{t('quelle.datei.keineDateiart')}</p>
      </Blattabschnitt>
    )
  }

  const probeart: 'xlsx' | 'text' | null = probe ? (probe.art === 'xlsx' ? 'xlsx' : 'text') : quelle.art === 'xlsx' ? 'xlsx' : 'text'
  const dateisatz = [
    abbildung.blatt ? t('quelle.satz.blatt', { blatt: abbildung.blatt }) : null,
    t('quelle.satz.kopf', { kopf: anzahlwort(abbildung.kopfzeilen), fuss: anzahlwort(abbildung.fusszeilen) }),
    abbildung.faktor !== 1 ? t('quelle.satz.faktor', { faktor: dezimalzahl(abbildung.faktor) }) : null,
    abbildung.vorzeichen === -1 ? t('quelle.satz.vorzeichen') : null,
  ]
    .filter((s): s is string => s !== null)
    .join(' · ')

  return (
    <>
      {/*
        **Ein Package liest nach der Vorlage** (R745, R1192): Blatt, Kopfzeilen,
        Spaltenzuordnung und Vorschläge wirken dort nicht. Es bleibt die eine
        Angabe, die die Vorlage offen lässt — die Bewertungsebene.
      */}
      {istPackage ? (
        <Packageabschnitt
          abbildung={abbildung}
          zielfelder={zielfelder}
          stand={stand}
          laeuft={laeuft}
          meldung={meldungsort === 'zuordnung' ? meldung : null}
          darfSpeichern={darf('abbildungSpeichern')}
          aufEbene={(wert) => aendere({ felder: { ...abbildung.felder, ledger: { wert } } })}
          aufSpeichern={() => void speichere()}
        />
      ) : (
        <>
        {/* ---- Die Datei ------------------------------------------------- */}
        <Blattabschnitt kennung="quelle.datei" titel={t('quelle.datei.titel')} zusatz={dateisatz}>
          {/*
            **Das Blatt spricht die Sprache des Blattes** (R515): dieselben vier
            Spalten wie die Eigenschaften darüber — Feld, Wert, dann die Spalte,
            in der dort die Herkunft steht und hier die Erläuterung, und die
            leere Handgriffspalte. Kein Fenstermuster mit Beschriftung über dem
            Feld; das ist die Sprache der Buchungsmaske.
          */}
          <div className="tabellenrahmen">
            <table ref={spaltenrahmen1} className="stammdatenfelder dateiraster ziehbaretabelle">
              <Spaltenmassleiste masse={spaltenmasse1} />
              <thead>
                <tr>
                  <th>{t('stammdaten.feld')}</th>
                  <th>{t('stammdaten.wert')}</th>
                  <th />
                  <th />
                </tr>
              </thead>
              <tbody>
                {/* Wer keine Datei lesen lassen darf, bekommt die Zeile nicht (R1180). */}
                {darf('dateiprobe') && (
                  <tr>
                    <td>
                      <label htmlFor="quelle-datei">{t('quelle.datei.beispiel')}</label>
                    </td>
                    <td>
                      <Dateiwahl
                        id="quelle-datei"
                        beschriftung={t('dateiwahl.waehlen')}
                        eingabe={dateifeld}
                        accept=".xlsx,.csv,.txt"
                        gesperrt={laeuft}
                        aufWahl={(dateien) => void lies(dateien[0])}
                      />
                    </td>
                    <td className="hinweiszelle">{t('quelle.datei.erlaeuterung')}</td>
                    <td className="handgriff" />
                  </tr>
                )}

                {probeart === 'xlsx' && (
                  <tr>
                    <td>
                      <label htmlFor="quelle-blatt">{t('quelle.blatt')}</label>
                    </td>
                    <td>
                      {probe?.blaetter && probe.blaetter.length > 0 ? (
                        <select
                          id="quelle-blatt"
                          value={abbildung.blatt}
                          onChange={(e) => {
                            aendere({ blatt: e.target.value })
                            void lies(datei.current, e.target.value)
                          }}
                        >
                          {probe.blaetter.map((b) => (
                            <option key={b} value={b}>
                              {b}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          id="quelle-blatt"
                          value={abbildung.blatt}
                          onChange={(e) => aendere({ blatt: e.target.value })}
                          placeholder="SuSa"
                        />
                      )}
                    </td>
                    <td className="hinweiszelle" />
                    <td className="handgriff" />
                  </tr>
                )}

                <tr>
                  <td>
                    <label htmlFor="quelle-kopfzeilen">{t('quelle.kopfzeilen')}</label>
                  </td>
                  <td>
                    <input
                      id="quelle-kopfzeilen"
                      type="number"
                      min={0}
                      value={abbildung.kopfzeilen}
                      onChange={(e) => aendere({ kopfzeilen: Math.max(0, Number(e.target.value)) })}
                    />
                  </td>
                  <td className="hinweiszelle">{t('quelle.kopfzeilen.erlaeuterung')}</td>
                  <td className="handgriff" />
                </tr>

                <tr>
                  <td>
                    <label htmlFor="quelle-fusszeilen">{t('quelle.fusszeilen')}</label>
                  </td>
                  <td>
                    <input
                      id="quelle-fusszeilen"
                      type="number"
                      min={0}
                      value={abbildung.fusszeilen}
                      onChange={(e) => aendere({ fusszeilen: Math.max(0, Number(e.target.value)) })}
                    />
                  </td>
                  <td className="hinweiszelle">{t('quelle.fusszeilen.erlaeuterung')}</td>
                  <td className="handgriff" />
                </tr>

                {/*
                  **Woher die Periode kommt** (R728, 5.1 Abschnitt 3.22): Ohne
                  diese Angabe stellt der Anwender die Periode beim Starten — für
                  einen Stapel aus 24 Dateien wären das 24 Handgriffe. Erkannt
                  wird sie nicht: Eine falsch geratene Periode ist eine gültige
                  Periode und fällt niemandem auf.
                */}
                <tr>
                  <td>
                    <label htmlFor="quelle-periodenherkunft">{t('quelle.periodenherkunft')}</label>
                  </td>
                  <td>
                    <select
                      id="quelle-periodenherkunft"
                      value={abbildung.periodenherkunft}
                      onChange={(e) =>
                        aendere({
                          periodenherkunft:
                            e.target.value === 'dateiname' || e.target.value === 'zelle' ? e.target.value : '',
                          // Das Muster steht bereit, sobald der Dateiname gewählt
                          // ist: Eine leere Klappliste wäre eine Frage ohne Antwort.
                          periodenmuster:
                            e.target.value === 'dateiname' && !abbildung.periodenmuster
                              ? PERIODENMUSTER[0]
                              : abbildung.periodenmuster,
                        })
                      }
                    >
                      <option value="">{t('quelle.periodenherkunft.keine')}</option>
                      <option value="dateiname">{t('quelle.periodenherkunft.dateiname')}</option>
                      <option value="zelle">{t('quelle.periodenherkunft.zelle')}</option>
                    </select>
                    {abbildung.periodenherkunft === 'dateiname' && (
                      <select
                        aria-label={t('quelle.periodenmuster')}
                        value={abbildung.periodenmuster || PERIODENMUSTER[0]}
                        onChange={(e) => aendere({ periodenmuster: e.target.value })}
                      >
                        {PERIODENMUSTER.map((m) => (
                          <option key={m} value={m}>
                            {m}
                          </option>
                        ))}
                      </select>
                    )}
                    {abbildung.periodenherkunft === 'zelle' && (
                      <input
                        aria-label={t('quelle.periodenzelle')}
                        value={abbildung.periodenzelle}
                        placeholder="B4"
                        size={4}
                        onChange={(e) => aendere({ periodenzelle: e.target.value.toUpperCase() })}
                      />
                    )}
                  </td>
                  <td className="hinweiszelle">
                    {abbildung.periodenherkunft === 'dateiname'
                      ? t('quelle.periodenherkunft.dateiname.erlaeuterung')
                      : abbildung.periodenherkunft === 'zelle'
                        ? t('quelle.periodenherkunft.zelle.erlaeuterung')
                        : t('quelle.periodenherkunft.erlaeuterung')}
                  </td>
                  <td className="handgriff" />
                </tr>

                {/*
                  **Was die Zahl ist, sagt die Quelle** (R540): ein Stand, aus dem
                  der Import die Verkehrszahl ableitet — oder schon die Bewegung
                  der Periode. Erkannt wird das nicht am Inhalt; im ersten Monat
                  gäbe es nichts zu vergleichen.
                */}
                <tr>
                  <td>
                    <label htmlFor="quelle-lieferart">{t('quelle.lieferart')}</label>
                  </td>
                  <td>
                    <select
                      id="quelle-lieferart"
                      value={abbildung.lieferart}
                      onChange={(e) => aendere({ lieferart: e.target.value === 'verkehrszahl' ? 'verkehrszahl' : 'saldo' })}
                    >
                      <option value="saldo">{t('quelle.lieferart.saldo')}</option>
                      <option value="verkehrszahl">{t('quelle.lieferart.verkehrszahl')}</option>
                    </select>
                  </td>
                  <td className="hinweiszelle">{t('quelle.lieferart.erlaeuterung')}</td>
                  <td className="handgriff" />
                </tr>

                {/*
                  **Wie der Betrag entsteht** (R580): Der native SuSa-Export gibt
                  oft keine Saldospalte her, sondern Soll und Haben — oder einen
                  Wert mit S/H-Kennzeichen. Die Betriebsart sagt, *wie* der Betrag
                  entsteht, nicht, *was* er bedeutet; das sagt die Lieferart darüber.
                */}
                <tr>
                  <td>
                    <label htmlFor="quelle-betragsart">{t('quelle.betragsart')}</label>
                  </td>
                  <td>
                    <select
                      id="quelle-betragsart"
                      value={abbildung.betragsart}
                      onChange={(e) =>
                        aendere({
                          betragsart:
                            e.target.value === 'sollhaben' ||
                            e.target.value === 'kennzeichen' ||
                            e.target.value === 'spiegel' ||
                            e.target.value === 'package'
                              ? e.target.value
                              : 'wert',
                        })
                      }
                    >
                      <option value="wert">{t('quelle.betragsart.wert')}</option>
                      <option value="sollhaben">{t('quelle.betragsart.sollhaben')}</option>
                      <option value="kennzeichen">{t('quelle.betragsart.kennzeichen')}</option>
                      {/* Ein Spiegel trägt je Zeile viele Beträge, einen je Bewegungsart (R740). */}
                      <option value="spiegel">{t('quelle.betragsart.spiegel')}</option>
                      {/* Eine ganze Mappe nach der Standardvorlage (R743, R745). */}
                      <option value="package">{t('quelle.betragsart.package')}</option>
                    </select>
                  </td>
                  <td className="hinweiszelle">{t('quelle.betragsart.erlaeuterung')}</td>
                  <td className="handgriff" />
                </tr>

                {/*
                  **Nummer und Name in einer Spalte** (R579): gewählt, nicht
                  erkannt — eine Trennung an der führenden Ziffernfolge
                  zerschnitte „A1200 Bank" still und falsch.
                */}
                <tr>
                  <td>
                    <label htmlFor="quelle-kontotrennung">{t('quelle.kontotrennung')}</label>
                  </td>
                  <td>
                    <select
                      id="quelle-kontotrennung"
                      value={abbildung.kontotrennung}
                      onChange={(e) => aendere({ kontotrennung: e.target.value })}
                    >
                      <option value="">{t('quelle.kontotrennung.aus')}</option>
                      {KONTOTRENNUNGEN.map((zeichen) => (
                        <option key={zeichen} value={zeichen}>
                          {zeichen === ' '
                            ? t('quelle.kontotrennung.leerzeichen')
                            : t('quelle.kontotrennung.zeichen', { zeichen })}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="hinweiszelle">{t('quelle.kontotrennung.erlaeuterung')}</td>
                  <td className="handgriff" />
                </tr>

                {probeart === 'text' && (
                  <>
                    <tr>
                      <td>
                        <label htmlFor="quelle-trennzeichen">{t('quelle.trennzeichen')}</label>
                      </td>
                      <td>
                        <input
                          id="quelle-trennzeichen"
                          value={abbildung.trennzeichen}
                          onChange={(e) => aendere({ trennzeichen: e.target.value })}
                        />
                      </td>
                      <td className="hinweiszelle" />
                      <td className="handgriff" />
                    </tr>
                    <tr>
                      <td>
                        <label htmlFor="quelle-dezimal">{t('quelle.dezimal')}</label>
                      </td>
                      <td>
                        <input
                          id="quelle-dezimal"
                          value={abbildung.dezimaltrennzeichen}
                          onChange={(e) => aendere({ dezimaltrennzeichen: e.target.value })}
                        />
                      </td>
                      <td className="hinweiszelle" />
                      <td className="handgriff" />
                    </tr>
                  </>
                )}

                <tr>
                  <td>
                    <label htmlFor="quelle-faktor">{t('quelle.faktor')}</label>
                  </td>
                  <td>
                    <input
                      id="quelle-faktor"
                      type="number"
                      value={abbildung.faktor}
                      onChange={(e) => aendere({ faktor: Number(e.target.value) || 1 })}
                    />
                  </td>
                  <td className="hinweiszelle">{t('quelle.faktor.erlaeuterung')}</td>
                  <td className="handgriff" />
                </tr>

                <tr>
                  <td>
                    <label htmlFor="quelle-vorzeichen">{t('quelle.vorzeichen')}</label>
                  </td>
                  <td>
                    <input
                      id="quelle-vorzeichen"
                      type="checkbox"
                      checked={abbildung.vorzeichen === -1}
                      onChange={(e) => aendere({ vorzeichen: e.target.checked ? -1 : 1 })}
                    />
                  </td>
                  <td className="hinweiszelle">{t('quelle.vorzeichen.erlaeuterung')}</td>
                  <td className="handgriff" />
                </tr>
              </tbody>
            </table>
          </div>

          {probe === null ? (
            <p className="hinweis">{t('quelle.datei.keine')}</p>
          ) : (
            <>
              {/*
                **Die Datei, wie sie dasteht — und zugeklappt, sobald sie
                gelesen ist** (5.1 Abschnitt 3.23, Entscheidung 2). Sie
                beantwortet genau eine Frage: ab welcher Zeile die Daten
                anfangen. Wer sie beantwortet hat, braucht zwölf Zeilen nicht
                dauerhaft im Bild — gegriffen wird auf der Quellseite der
                Zuordnung, nicht mehr hier.
              */}
              <details className="dateiprobeklappe">
                {/*
                  **Der Griff trägt einen Kasten und heißt nach dem, was er
                  zeigt** (R727, Befund des Auftraggebers vom 2026-09-25): Als
                  gedämpfte Zeile war er nicht als Bedienelement zu erkennen.
                  Der Dateiname steht daneben und nicht darin — er beschriftet
                  keinen Griff, er sagt, was gelesen wurde.
                */}
                <summary>
                  <span className="griffwort">{t('quelle.datei.vorschau')}</span>
                  {/* Ein Dateiname ist kein Periodentext — die Bildschirmprüfung liest ihn nicht (R839). */}
                  <span className="erlaeuterung" data-roh>
                    {t('quelle.datei.gelesen', { datei: probe.dateiname, zeilen: anzahlwort(probe.zeilen) })}
                  </span>
                </summary>
                <div className="tabellenrahmen">
                  <table ref={spaltenrahmen2} className="dateiprobe ziehbaretabelle">
                    <Spaltenmassleiste masse={spaltenmasse2} />
                    <thead>
                      <tr>
                        <th />
                        {Array.from({ length: probe.spalten }, (_, i) => (
                          <th key={i}>
                            <span className="buchstabe">{spaltenname(i)}</span>
                            <span className="kopf">{ueberschrift[i] || ''}</span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {probe.anfang.map((zeile, i) => (
                        <tr
                          key={i}
                          className={
                            i < abbildung.kopfzeilen - 1 ? 'kopfzeile' : i === abbildung.kopfzeilen - 1 ? 'ueberschrift' : undefined
                          }
                        >
                          <td className="gedaempft">{i + 1}</td>
                          {Array.from({ length: probe.spalten }, (_, j) => (
                            <td key={j}>{zeile[j] ?? ''}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            </>
          )}
        </Blattabschnitt>

        {/* ---- Die Zuordnung --------------------------------------------- */}
        <Blattabschnitt
          kennung="quelle.zuordnung"
          titel={t('quelle.zuordnung.titel')}
          zusatz={t('quelle.zuordnung.zusatz', {
            zugeordnet: anzahlwort(stand.zugeordnet),
            gesamt: anzahlwort(stand.zielfelder),
            nicht: anzahlwort(stand.nichtGeliefert),
            offen: anzahlwort(stand.offen),
          })}
        >
          {/*
            **Im Blatt steht die Auskunft, im Fenster die Arbeit** (R737, 5.1
            Abschnitt 3.24). Bis zum 2026-09-25 stand die ganze Zuordnung hier —
            zwölf Zeilen neben der Fassungsspalte, und die Quellseite blieb leer,
            sobald jemand die Quelle ohne Beispieldatei öffnete. Eine vergessene
            Zeile fiel darin nicht auf.
          */}
          <p className="zuordnungsauskunft">
            <button type="button" onClick={() => setFensterAuf(true)}>
              {t('quelle.zuordnung.oeffnen')}
            </button>
            <span className={stand.offen > 0 ? 'offen' : 'fertig'}>
              <Zustandssymbol art={stand.offen > 0 ? 'offen' : 'fertig'} />
              {stand.offen > 0
                ? t('quelle.zuordnung.offen', {
                    anzahl: anzahlwort(stand.offen),
                    felder: stand.offeneFelder.map((name) => feldnameVon(zielfelder, name)).join(', '),
                  })
                : t('quelle.zuordnung.vollstaendig')}
            </span>
          </p>

          {/*
            **Der Satz über die Quelle steht nicht mehr im Blatt** (Befund des
            Auftraggebers vom 2026-09-25). Er fasste zusammen, was zwei Zeilen
            darüber schon dasteht — Blatt, Kopfzeilen, jede gelegte Spalte —,
            und beantwortete damit keine Frage, die der Bildschirm nicht schon
            beantwortet hatte. `satzUeberQuelle` bleibt als Auskunft erhalten
            (5.1 Abschnitt 3.7, Grenze aus R136), zeigt sie aber niemand mehr.
          */}
          {darf('abbildungSpeichern') && (
            <p className="handreichung">
              <button type="button" className="haupt" disabled={laeuft || !stand.pflichtfelder} onClick={() => void speichere()}>
                {t('quelle.speichern')}
              </button>
              {!stand.pflichtfelder && <span className="erlaeuterung"> {t('quelle.nochnicht')}</span>}
            </p>
          )}
          {meldung && meldungsort === 'zuordnung' && <p className="hinweis">{meldung}</p>}
        </Blattabschnitt>

        {/* ---- Vorschläge zur Bewegungsart (5.1 Abschnitt 3.35) ----------- */}
        <Blattabschnitt
          kennung="quelle.vorschlaege"
          titel={t('quelle.vorschlaege.titel')}
          vorgabe={false}
          zusatz={vorschlagszusatz(abbildung.vorschlaege.filter(vollstaendigerVorschlag).length)}
        >
          <Vorschlagsliste
            vorschlaege={abbildung.vorschlaege}
            bewegungsarten={zielfelder.find((z) => z.name === 'bewegungsart')?.werte ?? []}
            // Default als fester Wert heißt gerade „keine geliefert" (R541) — dann greifen sie.
            festeBewegungsart={Boolean(abbildung.felder['bewegungsart']?.wert) && abbildung.felder['bewegungsart']?.wert !== 'BWA-DEF'}
            laeuft={laeuft}
            aufAendern={(vorschlaege) => aendere({ vorschlaege })}
          />
          {/*
            Gespeichert wird mit der Quelle, als ihre nächste Fassung (R521, R871).
            Ein Kasten und keine gefüllte Fläche: Die eine gefüllte steht an der
            Spaltenzuordnung (R727).
          */}
          {darf('abbildungSpeichern') && (
            <p className="handreichung">
              <button
                type="button"
                disabled={laeuft || !stand.pflichtfelder || !abbildung.vorschlaege.every(vollstaendigerVorschlag)}
                onClick={() => void speichere(abbildung, 'vorschlaege')}
              >
                {t('quelle.speichern')}
              </button>
              {!abbildung.vorschlaege.every(vollstaendigerVorschlag) && (
                <span className="erlaeuterung"> {t('quelle.vorschlaege.unvollstaendig')}</span>
              )}
            </p>
          )}
          {meldung && meldungsort === 'vorschlaege' && <p className="hinweis">{meldung}</p>}
        </Blattabschnitt>
        </>
      )}

      {/* ---- Verwendung ------------------------------------------------ */}
      <Blattabschnitt
        kennung="quelle.verwendung"
        titel={t('quelle.verwendung.titel')}
        vorgabe={false}
        // **Zeitpunkt statt Fassungsnummer, keine Zahl der Lieferungen** (7.2
        // Abschnitt 36, R1141, R1143) — bis zum 2026-10-06 „Fassung 3 · 12
        // Lieferungen". Dass noch keine kam, ist ein Befund und bleibt.
        zusatz={
          laeufe && laeufe.length > 0
            ? t('quelle.verwendung.zusatz', { zeit: zeitpunkt(geaendertAm) })
            : t('quelle.verwendung.zusatz.keine', { zeit: zeitpunkt(geaendertAm) })
        }
      >
        {laeufe && laeufe.length > 0 ? (
          <div className="tabellenrahmen">
            <table ref={spaltenrahmen3} className="ziehbaretabelle">
              <Spaltenmassleiste masse={spaltenmasse3} />
              <thead>
                {/* Ohne Laufnummer und ohne Zeilenzahl — wie die Historie eines
                    Datenimports (7.2 Abschnitte 34 und 36, R1135, R1143). */}
                <tr>
                  <th>{t('import.spalte.einheit')}</th>
                  <th>{t('kontext.ledger')}</th>
                  <th>{t('import.spalte.periode')}</th>
                  {/* Mit dem Anwender, wie jedes Protokoll (7.2 Abschnitt 42, R1270). */}
                  <th>{t('protokoll.spalte.anwender')}</th>
                  <th>{t('import.spalte.wann')}</th>
                </tr>
              </thead>
              <tbody>
                {laeufe.map((l) => (
                  <tr key={l.id}>
                    {/* Gesellschaft und Bewertungsebene beim Namen, nicht als Kennung (R838). */}
                    <td>{gesellschaftsname(l.einheit)}</td>
                    <td>{bewertungsebenenname(l.ledger)}</td>
                    <td>{periodenname(l.periode)}</td>
                    <td>{anwendername(l.erfasstVon)}</td>
                    <td className="gedaempft">{zeitpunkt(l.erfasstAm)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="hinweis">{t('quelle.verwendung.leer')}</p>
        )}
      </Blattabschnitt>

      {/* Die Arbeit geschieht im Fenster, im Blatt steht ihre Auskunft (R737). */}
      {fensterAuf && (
        <Spaltenfenster
          zielfelder={zielfelder}
          abbildung={abbildung}
          probe={probe}
          laeuft={laeuft}
          aufLesen={(gewaehlt) => void lies(gewaehlt)}
          aufUebernehmen={(felder, spiegelspalten) => void uebernimm(felder, spiegelspalten)}
          aufSchliessen={() => setFensterAuf(false)}
        />
      )}
    </>
  )
}

/**
 * **Was an einer Package-Quelle einzustellen ist** (R1190–R1192): die
 * Bewertungsebene. Alles andere legt die Vorlage fest, und den Zuschnitt
 * ändert man dort, wo er entsteht — unter *Package-Vorlagen*.
 */
function Packageabschnitt({
  abbildung,
  zielfelder,
  stand,
  laeuft,
  meldung,
  darfSpeichern,
  aufEbene,
  aufSpeichern,
}: {
  abbildung: Abbildung
  zielfelder: Zielfeld[]
  stand: { offen: number; pflichtfelder: boolean }
  laeuft: boolean
  meldung: string | null
  darfSpeichern: boolean
  aufEbene: (wert: string) => void
  aufSpeichern: () => void
}) {
  // Die Spaltenbreiten zieht der Anwender (7.3 Abschnitt 10, R415).
  const { masse, rahmen } = useSpaltenmasse<HTMLTableElement>('datenquelle:packageabschnitt')
  const ebenen = zielfelder.find((z) => z.name === 'ledger')?.werte ?? []
  const fertig = stand.offen === 0

  return (
    <Blattabschnitt
      kennung="quelle.package"
      titel={t('quelle.package.titel')}
      zusatz={fertig ? t('quelle.package.fertig') : t('quelle.package.offen')}
    >
      <div className="tabellenrahmen">
        <table ref={rahmen} className="stammdatenfelder ziehbaretabelle">
          <Spaltenmassleiste masse={masse} />
          <tbody>
            <tr>
              <td>{t('quelle.betragsart')}</td>
              <td>{t('quelle.betragsart.package')}</td>
              <td className="hinweiszelle">{t('quelle.package.vorlage')}</td>
              <td className="handgriff" />
            </tr>
            <tr>
              <td>{t('kontext.ledger')}</td>
              <td>
                <Auswahlfeld
                  sorte="aufzaehlung"
                  wert={abbildung.felder.ledger?.wert ?? ''}
                  beschriftung={t('kontext.ledger')}
                  leer={t('quelle.feld.wertwahl')}
                  eintraege={ebenen.map((w) => ({
                    wert: w.schluessel,
                    text: vorratsname(w),
                    suchtext: w.schluessel,
                    stillgelegtAb: w.stillgelegtAb,
                  }))}
                  aufWahl={(wert) => wert !== '' && aufEbene(wert)}
                />
              </td>
              <td className="hinweiszelle">{t('quelle.package.ebene')}</td>
              <td className="handgriff" />
            </tr>
          </tbody>
        </table>
      </div>
      {darfSpeichern && (
        <p className="handreichung">
          <button type="button" className="haupt" disabled={laeuft || !stand.pflichtfelder} onClick={aufSpeichern}>
            {t('quelle.speichern')}
          </button>
        </p>
      )}
      {meldung && <p className="hinweis">{meldung}</p>}
    </Blattabschnitt>
  )
}

function feldnameVon(zielfelder: Zielfeld[], name: string): string {
  const z = zielfelder.find((f) => f.name === name)
  return z ? feldname(z) : name
}

/**
 * **Das Fenster, in dem zugeordnet wird** (R737, 5.1 Abschnitt 3.24).
 *
 * Es arbeitet auf einer **Kopie der Feldbindungen**: *Übernehmen* gibt sie
 * zurück und speichert, *Abbrechen* lässt den Stand vor dem Öffnen gelten.
 * Geändert wird hier nur die Zuordnung — Kopfzeilen, Trennzeichen und Blatt
 * bleiben Sache des Blattes, auch wenn im Fenster eine Datei gelesen wird.
 *
 * Die Anordnung ist die aus R734: Quelle links, Ziel rechts, jede Seite rollt
 * für sich. Sie ist am 2026-09-25 aus dem Blatt hierher gezogen, weil sie im
 * Blatt neben der Fassungsspalte nie die Breite hatte, die sie braucht.
 */
export function Spaltenfenster({
  zielfelder,
  abbildung,
  probe,
  laeuft,
  aufLesen,
  aufUebernehmen,
  aufSchliessen,
}: {
  zielfelder: Zielfeld[]
  abbildung: Abbildung
  probe: Dateiprobe | null
  laeuft: boolean
  aufLesen: (datei: File | null | undefined) => void
  aufUebernehmen: (felder: Record<string, Feldbindung>, spiegelspalten: Record<number, string>) => void
  aufSchliessen: () => void
}) {
  const darf = useDarf()
  const [felder, setFelder] = useState<Record<string, Feldbindung>>(abbildung.felder)
  const [spiegelspalten, setSpiegelspalten] = useState<Record<number, string>>(abbildung.spiegelspalten)
  const [gegriffen, setGegriffen] = useState<number | null>(null)

  const entwurf = useMemo(() => ({ ...abbildung, felder, spiegelspalten }), [abbildung, felder, spiegelspalten])

  /*
    **Im Spiegel trägt jede Wertspalte ihre Bewegungsart** (R740) — statt eines
    Betrags je Zeile stehen dort viele, einer je Spalte. Der Vorrat ist der des
    Zielfeldes: Was im Katalog steht, steht hier zur Wahl.
  */
  const istSpiegel = entwurf.betragsart === 'spiegel'
  const bewegungsarten = zielfelder.find((z) => z.name === 'bewegungsart')?.werte ?? []

  function setzeSpiegelspalte(stelle: number, art: string) {
    setSpiegelspalten((bisher) => {
      const naechste = { ...bisher }
      if (art) naechste[stelle] = art
      else delete naechste[stelle]
      return naechste
    })
  }
  const stand = useMemo(() => zuordnungsstand(zielfelder, entwurf), [zielfelder, entwurf])
  const ueberschrift = probe?.anfang[Math.max(0, entwurf.kopfzeilen - 1)] ?? []
  const beispielzeile = probe?.anfang[entwurf.kopfzeilen] ?? []
  const spalten = useMemo(
    () => (probe === null ? [] : spaltenzeilen(probe.spalten, ueberschrift, beispielzeile, entwurf, zielfelder)),
    [probe, ueberschrift, beispielzeile, entwurf, zielfelder],
  )

  function binde(feld: string, bindung: Feldbindung | null) {
    setFelder((bisher) => {
      const naechste = { ...bisher }
      if (bindung === null) {
        delete naechste[feld]
      } else {
        naechste[feld] = bindung
      }
      return naechste
    })
    setGegriffen(null)
  }

  /** Eine Spalte auf ein Feld legen — gezogen oder geklickt (R306); die Überschrift reist mit (R525). */
  function lege(feld: string, spalte: number) {
    const kopf = ueberschrift[spalte]?.trim()
    binde(feld, kopf ? { spalte, kopf } : { spalte })
  }

  return (
    <Dialogfenster
      titel={t('quelle.zuordnung.titel')}
      kopfzusatz={t('quelle.zuordnung.zusatz', {
        zugeordnet: anzahlwort(stand.zugeordnet),
        gesamt: anzahlwort(stand.zielfelder),
        nicht: anzahlwort(stand.nichtGeliefert),
        offen: anzahlwort(stand.offen),
      })}
      weite="breit"
      aufSchliessen={aufSchliessen}
      fuss={
        <>
          {/* Übernehmen speichert die Quelle — ohne das Recht bleibt nur Schließen (R1180). */}
          {darf('abbildungSpeichern') && (
            <button
              type="button"
              className="haupt"
              disabled={laeuft || !stand.pflichtfelder}
              onClick={() => aufUebernehmen(felder, spiegelspalten)}
            >
              {t('quelle.zuordnung.uebernehmen')}
            </button>
          )}
          <button type="button" onClick={aufSchliessen}>
            {t('quelle.zuordnung.abbrechen')}
          </button>
          {darf('abbildungSpeichern') && !stand.pflichtfelder && (
            <span className="erlaeuterung">{t('quelle.nochnicht')}</span>
          )}
        </>
      }
    >
      {/*
        **Das Fenster fragt selbst nach der Beispieldatei** (R737): Sie wird
        nicht mitgespeichert, und wer eine gespeicherte Quelle aufschlägt, hat
        sonst eine leere Quellseite vor sich. Ohne Datei bleibt alles lesbar
        und stilllegbar — nur neu legen lässt sich nichts, was nicht dasteht.
      */}
      <p className="fensterdatei">
        {darf('dateiprobe') && (
          <>
            <label htmlFor="quelle-fensterdatei">{t('quelle.datei.beispiel')}</label>
            {/* Ohne Namen daneben: Der Satz dahinter sagt, welche Datei gelesen ist. */}
            <Dateiwahl
              id="quelle-fensterdatei"
              beschriftung={t('dateiwahl.waehlen')}
              accept=".xlsx,.csv,.txt"
              gesperrt={laeuft}
              ohneNamen
              aufWahl={(dateien) => aufLesen(dateien[0])}
            />
          </>
        )}
        <span className="erlaeuterung">
          {probe === null
            ? t('quelle.zuordnung.ohnedatei')
            : t('quelle.datei.gelesen', { datei: probe.dateiname, zeilen: anzahlwort(probe.zeilen) })}
        </span>
      </p>

      <div className="zuordnungnebeneinander">
        <div className="quellseite">
          <h3>{t('quelle.spalten.titel')}</h3>
          {istSpiegel && <p className="erlaeuterung">{t('quelle.spiegel.hinweis')}</p>}
          {probe === null ? (
            <p className="erlaeuterung">{t('quelle.spalten.ohnedatei')}</p>
          ) : (
            <ul className={`spaltenliste ${istSpiegel ? 'mitbewegungsart' : ''}`.trim()}>
              {spalten.map((s) => (
                <li key={s.stelle}>
                  <button
                    type="button"
                    className={`spaltenchip ${gegriffen === s.stelle ? 'gegriffen' : ''} ${s.ziele.length > 0 ? 'belegt' : ''}`.trim()}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData('text/plain', String(s.stelle))
                      setGegriffen(s.stelle)
                    }}
                    onDragEnd={() => setGegriffen(null)}
                    onClick={() => setGegriffen((jetzt) => (jetzt === s.stelle ? null : s.stelle))}
                  >
                    <span className="buchstabe">{s.buchstabe}</span>
                    <span className="kopf">{s.kopf}</span>
                    <span className="beispiel">{s.beispiel}</span>
                    {/* Eine vergebene Spalte bleibt stehen und nennt ihr Ziel (R735). */}
                    {s.ziele.length > 0 && (
                      <span className="ziel">{t('quelle.spalte.belegt', { feld: s.ziele.join(', ') })}</span>
                    )}
                  </button>

                  {/*
                    **Die Bewegungsart steht an der Spalte** (R740). Eine
                    gerechnete Größe darf gewählt werden — sie trägt dann die
                    Probe und wird nicht gebucht (R741); die Zeile sagt es
                    daneben, damit niemand sie für eine Bewegung hält.
                  */}
                  {istSpiegel && (
                    <span className="spaltenbewegungsart">
                      <Auswahlfeld
                        sorte="verweis"
                        wert={spiegelspalten[s.stelle] ?? ''}
                        beschriftung={t('quelle.spiegel.bewegungsart')}
                        leer={t('quelle.spiegel.bewegungsart')}
                        // **Der Vorrat kennt keinen Vortrag** (R1061); die
                        // Probe des Anfangsbestands steht eigens da, ohne den
                        // Namen einer Bewegungsart.
                        eintraege={[
                          {
                            wert: BEWEGUNGSART_ANFANGSBESTAND,
                            text: t('quelle.spiegel.anfangsbestand'),
                            suchtext: BEWEGUNGSART_ANFANGSBESTAND,
                          },
                          ...ohneVortrag(bewegungsarten).map((w) => ({
                            wert: w.schluessel,
                            text: vorratsname(w),
                            suchtext: w.schluessel,
                            stillgelegtAb: w.stillgelegtAb,
                          })),
                        ]}
                        aufWahl={(wert) => setzeSpiegelspalte(s.stelle, wert)}
                      />
                      {istGerechneteBewegungsart(spiegelspalten[s.stelle] ?? '') && (
                        <span className="probemarke">{t('quelle.spiegel.probe')}</span>
                      )}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Die Betragszeilen hängen an der Betriebsart (R580) — deshalb reist die Spaltenzuordnung mit. */}
        <div className="zielseite">
          {gruppen(zielfelder, entwurf).map(({ gruppe, felder: zeilen }) => {
            const zugeordnet = zeilen.filter((z) => feldzustand(felder[z.name]) === 'zugeordnet').length
            // Der Gruppenkopf bekommt den Haken, sobald keine seiner Zeilen
            // mehr offen ist (R738) — „nicht geliefert" ist entschieden.
            const gruppeSteht = zeilen.every((z) => feldzustand(felder[z.name]) !== 'offen')
            return (
              <div key={gruppe} className="zielfeldgruppe">
                <h3>
                  {gruppeSteht && <Zustandssymbol art="fertig" />}
                  {t(`quelle.gruppe.${gruppe}` as Sprachschluessel)}
                  <span className="zaehlung">{t('quelle.gruppe.zaehlung', { zugeordnet: anzahlwort(zugeordnet), gesamt: anzahlwort(zeilen.length) })}</span>
                </h3>
                <ul className="zielfeldliste">
                  {zeilen.map((z) => (
                    <Zielfeldzeile
                      key={z.name}
                      feld={z}
                      bindung={felder[z.name]}
                      ueberschrift={ueberschrift}
                      beispielzeile={beispielzeile}
                      gegriffen={gegriffen}
                      spalten={spalten}
                      aufSpalte={(spalte) => lege(z.name, spalte)}
                      aufWert={(wert) => binde(z.name, wert ? { wert } : null)}
                      aufNicht={(nicht) => binde(z.name, nicht ? { nicht: true } : null)}
                      aufLoesen={() => binde(z.name, null)}
                    />
                  ))}
                </ul>
              </div>
            )
          })}
        </div>
      </div>
    </Dialogfenster>
  )
}

/**
 * Ein Zielfeld mit seinen drei Zuständen (R523) und seinen Griffen: Spalte
 * ablegen, Spalte aus der Liste wählen (R736), festen Wert wählen (R524), als
 * „nicht geliefert" abhaken, lösen.
 */
function Zielfeldzeile({
  feld,
  bindung,
  ueberschrift,
  beispielzeile,
  gegriffen,
  spalten,
  aufSpalte,
  aufWert,
  aufNicht,
  aufLoesen,
}: {
  feld: Zielfeld
  bindung: Feldbindung | undefined
  ueberschrift: string[]
  beispielzeile: string[]
  gegriffen: number | null
  /** Die Spalten der Datei für die Klappliste (R736) — leer, solange keine gelesen ist. */
  spalten: Spaltenzeile[]
  aufSpalte: (spalte: number) => void
  aufWert: (wert: string) => void
  aufNicht: (nicht: boolean) => void
  aufLoesen: () => void
}) {
  const zustand = feldzustand(bindung)
  const name = feldname(feld)

  let wert: React.ReactNode
  if (bindung?.spalte !== undefined) {
    const kopf = bindung.kopf ?? ueberschrift[bindung.spalte] ?? ''
    const beispiel = beispielzeile[bindung.spalte]
    wert = (
      <>
        <span className="code">{spaltenname(bindung.spalte)}</span>
        {kopf ? ` „${kopf}"` : ''}
        {beispiel ? <span className="beispiel">{t('quelle.feld.beispiel', { wert: beispiel })}</span> : null}
      </>
    )
  } else if (bindung?.wert) {
    const gewaehlt = feld.werte.find((w) => w.schluessel === bindung.wert)
    wert = t('quelle.feld.wert', { wert: gewaehlt ? vorratsname(gewaehlt) : bindung.wert })
  } else if (zustand === 'nichtgeliefert') {
    wert = t('quelle.feld.nichtgeliefert')
  } else {
    wert = t('quelle.feld.offen')
  }

  return (
    <li
      className={`zielfeld ${zustand} ${gegriffen !== null ? 'bereit' : ''}`.trim()}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault()
        const spalte = Number(e.dataTransfer.getData('text/plain'))
        if (!Number.isNaN(spalte)) {
          aufSpalte(spalte)
        }
      }}
      onClick={() => gegriffen !== null && aufSpalte(gegriffen)}
    >
      {/*
        **Jede Zeile trägt ihren Zustand als Zeichen** (R738). Ein kursives
        Wort in der Mitte der Zeile ist kein Zustand, den man zählt — daran
        ist am 2026-09-25 eine vergessene Zeile durchgerutscht. „Nicht
        geliefert" bekommt die leere Form: entschieden, aber nichts kommt an.
      */}
      <span className="name">
        <Zustandssymbol art={zustand === 'zugeordnet' ? 'fertig' : zustand === 'nichtgeliefert' ? 'fehlt' : 'offen'} />
        {name}
        {feld.pflicht && <span className="pflichtmarke">{t('quelle.feld.pflicht')}</span>}
      </span>
      <span className={`wert ${zustand}`}>{wert}</span>
      <span className="griffe" onClick={(e) => e.stopPropagation()}>
        {/*
          **Jedes Zielfeld nimmt seine Spalte auch aus einer Liste** (R736).
          Ziehen bleibt der schnelle Weg; die Liste ist der, der ohne Maus
          geht und ohne dass die Quellseite im Bild sein muss. Jeder Eintrag
          trägt Buchstabe, Kopfzeile und den ersten Wert — ohne den wählt man
          blind.
        */}
        {spalten.length > 0 && (
          <Auswahlfeld
            sorte="verweis"
            className="spaltenwahl"
            /*
              **Das Feld bleibt der Griff und wird nicht zur zweiten Anzeige**
              — welche Spalte liegt, steht links in derselben Zeile. Deshalb
              steht hier immer der Platzhalter; gelöst wird mit „lösen“, und
              die leere Wahl tut nichts.
            */
            wert=""
            beschriftung={t('quelle.feld.spaltenwahl')}
            leer={t('quelle.feld.spaltenwahl')}
            eintraege={spalten.map((s) => ({
              wert: String(s.stelle),
              text: [s.buchstabe, s.kopf, s.beispiel].filter((teil) => teil !== '').join(' · '),
              suchtext: `${s.buchstabe} ${s.kopf} ${s.beispiel}`,
            }))}
            aufWahl={(wert) => wert !== '' && aufSpalte(Number(wert))}
          />
        )}
        {!feld.nurSpalte && feld.werte.length > 0 && (
          /*
            **Der feste Wert eines Zielfeldes** — und er sucht erst ab zwölf
            (R583). Heute sind es überall drei oder vier Ausprägungen aus den
            Prüfregeln der Datenbank; das Feld bleibt damit die Klappliste, die
            es war. Wächst ein Vorrat später — die Währung ist der Anwärter —,
            entscheidet es selbst.
          */
          <Auswahlfeld
            sorte="aufzaehlung"
            wert={bindung?.wert ?? ''}
            beschriftung={t('quelle.feld.wertwahl')}
            leer={t('quelle.feld.wertwahl')}
            eintraege={feld.werte.map((w) => ({
              wert: w.schluessel,
              text: vorratsname(w),
              suchtext: w.schluessel,
              stillgelegtAb: w.stillgelegtAb,
            }))}
            aufWahl={aufWert}
          />
        )}
        {!feld.pflicht && zustand !== 'zugeordnet' && (
          <button type="button" className="schmal" onClick={() => aufNicht(zustand !== 'nichtgeliefert')}>
            {t(zustand === 'nichtgeliefert' ? 'quelle.feld.offen.knopf' : 'quelle.feld.nicht.knopf')}
          </button>
        )}
        {zustand === 'zugeordnet' && (
          <button type="button" className="schmal" onClick={aufLoesen}>
            {t('quelle.feld.loesen')}
          </button>
        )}
      </span>
    </li>
  )
}
